/**
 * Error Boundary for QuestionField component
 */

import { css } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from '../../styles';

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

interface QuestionFieldErrorBoundaryProps {
	children: React.ReactNode;
	questionIndex: number;
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export class QuestionFieldErrorBoundary extends React.Component<QuestionFieldErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: QuestionFieldErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error(`QuestionField Error (Question ${this.props.questionIndex + 1}):`, error, errorInfo);
		this.props.onError?.(error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			return (
				<section className={css(styleSheet.bottomSection, styleSheet.bottomSectionNoValue)}>
					<div className={css(styleSheet.section, styleSheet.sectionFullWidth)}>
						<div style={{ padding: '16px', textAlign: 'center' }}>
							<h3>Error loading question {this.props.questionIndex + 1}</h3>
							<p>There was an error loading this question. Please try refreshing the page.</p>
							{process.env.NODE_ENV === 'development' && this.state.error && (
								<details style={{ marginTop: '16px', textAlign: 'left' }}>
									<summary>Error Details</summary>
									<pre style={{ fontSize: '12px', color: '#666' }}>{this.state.error.stack}</pre>
								</details>
							)}
						</div>
					</div>
				</section>
			);
		}

		return this.props.children;
	}
}
